import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { BookMarked, BookOpen, MessageCircleQuestion, MoveRight } from "lucide-react";
import api, { API } from "@/lib/api";

function ScripturePreview({ m }) {
  const parts = /^(.*?)\s(\d+)(?::(\d+))?$/.exec(m.label || "") || [];
  const book = parts[1], chapter = parts[2], verse = parts[3];
  const { data } = useQuery({
    queryKey: ["bible", book, Number(chapter), "KJV"],
    queryFn: async () => (await api.get(`/bible/${encodeURIComponent(book)}/${chapter}`, { params: { translation: "KJV" } })).data,
    enabled: !!book && !!chapter,
    staleTime: 10 * 60 * 1000,
  });
  const verses = data?.verses || [];
  const shown = verse ? verses.filter((v) => v.verse === Number(verse)) : verses.slice(0, 3);
  return (
    <div data-testid="scripture-preview">
      <p className="no-reftag flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gold"><BookOpen className="h-3 w-3" /> {m.label} · KJV</p>
      {!data ? <div className="mt-2 h-10 animate-pulse rounded bg-muted" /> : (
        <div className="no-reftag mt-2 space-y-1.5 font-serif text-[15px] leading-snug text-foreground">
          {shown.map((v) => <p key={v.verse}><sup className="mr-1 font-mono text-[9px] text-muted-foreground">{v.verse}</sup>{v.text}</p>)}
          {!verse && verses.length > 3 && <p className="text-xs text-muted-foreground">…and {verses.length - 3} more verses</p>}
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Click to open in the Bible Reader</p>
    </div>
  );
}

function QuestionPreview({ m }) {
  const { data: q } = useQuery({ queryKey: ["question", m.id], queryFn: async () => (await api.get(`/questions/${m.id}`)).data, staleTime: 10 * 60 * 1000 });
  return (
    <div data-testid="question-preview">
      <p className="no-reftag flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gold"><MessageCircleQuestion className="h-3 w-3" /> {m.label}</p>
      {!q ? <div className="mt-2 h-10 animate-pulse rounded bg-muted" /> : (
        <>
          <p className="mt-2 font-serif text-lg italic leading-tight text-foreground">“{q.question_kjv}”</p>
          <p className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground">{q.who_asked?.join(", ")} <MoveRight className="h-3 w-3 text-gold" /> {q.asked_to?.join(", ")}</p>
          {q.scene && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{q.scene}</p>}
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Click to open the question</p>
    </div>
  );
}

function CommentaryPreview({ m }) {
  const [slug, sid] = (m.id || "").split("/");
  const { data: s } = useQuery({ queryKey: ["commentary-section", slug, sid], queryFn: async () => (await api.get(`/commentaries/${slug}/section/${sid}`)).data, enabled: !!slug && !!sid, staleTime: 10 * 60 * 1000 });
  return (
    <div data-testid="commentary-preview">
      <p className="no-reftag flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gold"><BookMarked className="h-3 w-3" /> {m.label}</p>
      {!s ? <div className="mt-2 h-10 animate-pulse rounded bg-muted" /> : (
        <>
          {s.title && <p className="mt-2 font-serif text-lg leading-tight text-foreground">{s.title}</p>}
          <p className="mt-2 line-clamp-4 font-serif text-[14px] leading-snug text-foreground/85">{s.paragraphs[0]}</p>
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Click to open the commentary</p>
    </div>
  );
}

export function MentionChip({ attrs }) {
  const isQ = attrs.kind === "question";
  const isC = attrs.kind === "commentary";
  return (
    <HoverCard openDelay={250} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link to={attrs.href || "#"} data-testid={`mention-chip-${attrs.kind}`} className={`no-reftag journal-mention journal-mention-${attrs.kind || "x"}`}>
          {isQ ? <MessageCircleQuestion className="mr-1 inline h-3.5 w-3.5 align-[-2px]" /> : isC ? <BookMarked className="mr-1 inline h-3.5 w-3.5 align-[-2px]" /> : <BookOpen className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />}{attrs.label}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 rounded-xl border-border bg-card p-4 shadow-xl">
        {isQ ? <QuestionPreview m={attrs} /> : isC ? <CommentaryPreview m={attrs} /> : <ScripturePreview m={attrs} />}
      </HoverCardContent>
    </HoverCard>
  );
}

function renderMarks(node, key) {
  let el = node.text;
  (node.marks || []).forEach((mk) => {
    if (mk.type === "bold") el = <strong>{el}</strong>;
    else if (mk.type === "italic") el = <em>{el}</em>;
    else if (mk.type === "strike") el = <s>{el}</s>;
    else if (mk.type === "underline") el = <u>{el}</u>;
    else if (mk.type === "link" && /^https?:\/\//i.test(mk.attrs?.href || "")) el = <a href={mk.attrs.href} target="_blank" rel="nofollow noopener noreferrer" className="underline decoration-gold/60 underline-offset-2 hover:text-gold">{el}</a>;
  });
  return <span key={key}>{el}</span>;
}

export function renderNode(node, key = 0) {
  if (!node) return null;
  const kids = (node.content || []).map((c, i) => renderNode(c, i));
  switch (node.type) {
    case "doc": return <>{kids}</>;
    case "paragraph": return <p key={key}>{kids.length ? kids : <br />}</p>;
    case "heading": return node.attrs?.level === 2 ? <h2 key={key}>{kids}</h2> : <h3 key={key}>{kids}</h3>;
    case "bulletList": return <ul key={key}>{kids}</ul>;
    case "orderedList": return <ol key={key}>{kids}</ol>;
    case "listItem": return <li key={key}>{kids}</li>;
    case "blockquote": return <blockquote key={key}>{kids}</blockquote>;
    case "hardBreak": return <br key={key} />;
    case "text": return renderMarks(node, key);
    case "mention": return <MentionChip key={key} attrs={node.attrs || {}} />;
    case "blogImage": return /^[a-f0-9]{32}$/.test(node.attrs?.id || "") ? (
      <figure key={key} className="my-6" data-testid="blog-image">
        <img src={`${API}/blog/images/${node.attrs.id}`} alt={node.attrs.alt || ""} loading="lazy" className="mx-auto max-h-[70vh] rounded-2xl object-contain" />
        {node.attrs.caption && <figcaption className="mt-2 text-center text-xs text-muted-foreground">{node.attrs.caption}</figcaption>}
      </figure>
    ) : null;
    default: return <span key={key}>{kids}</span>;
  }
}

export default function JournalContent({ doc, className = "", testId = "journal-content" }) {
  return <div className={`journal-prose ${className}`} data-testid={testId}>{renderNode(doc)}</div>;
}
